import { type FormSubmissionCreate, type ViolationProposalCreate } from "../types";
import { getRow, runWrite } from "./db";
import { type SubmissionRow } from "./outbox";

/**
 * The browser store's cure for a queued inspection the utility parked
 * `blocked` over a refused violation proposal: the tester's fixed list goes
 * onto the sealed payload and the document returns to the queue. A separate
 * object over the same database, never a member of `createIndexedDbStorage`'s
 * adapter — the storage contract, its shared conformance suite and the device
 * store carry no counterpart, because only the tester portal records
 * proposals.
 */
export interface ProposalCureStore {
  /**
   * In one transaction, and only from `blocked` (any other state, or no row,
   * writes nothing): the payload's `violation_proposals` is replaced by
   * `proposals`, or removed when `proposals` is `undefined` — see
   * `curedViolationProposals` for which the cure sends — and the row returns
   * to `finalized` with its blocked requirements cleared, exactly as a cured
   * change-request amend lands it. Parse-then-stringify of a `JSON.stringify`
   * product is the identity, so every other payload member — the sealed
   * `responses`, `device_of_record`, `change_requests` — stays byte-identical;
   * a proposal names no file, so no attachment row is touched.
   */
  replaceBlockedViolationProposals(
    clientSubmissionId: string,
    proposals: ViolationProposalCreate[] | undefined,
    now?: number,
  ): Promise<void>;
}

export function createIndexedDbProposalCure(db: IDBDatabase): ProposalCureStore {
  return {
    async replaceBlockedViolationProposals(clientSubmissionId, proposals, now = Date.now()) {
      await runWrite(db, ["outbox_submissions"], "enqueue", async (tx) => {
        const submissions = tx.objectStore("outbox_submissions");
        const row = await getRow<SubmissionRow>(submissions, clientSubmissionId);
        if (row === null || row.state !== "blocked") return;
        // payload_json was written as JSON.stringify of a FormSubmissionCreate.
        const payload = JSON.parse(row.payload_json) as FormSubmissionCreate;
        if (proposals === undefined) {
          delete payload.violation_proposals;
        } else {
          payload.violation_proposals = proposals;
        }
        row.payload_json = JSON.stringify(payload);
        row.state = "finalized";
        row.blocked_requirements = null;
        row.updated_at = now;
        submissions.put(row);
      });
    },
  };
}
